import React, { useState, FormEvent, useEffect, useContext, KeyboardEvent } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { toast } from 'react-toastify';
import { Auth, API } from 'aws-amplify';
import { DateTime } from 'ts-luxon';
import { useTranslation } from 'next-i18next';
import { ISignUpResult } from 'amazon-cognito-identity-js';

import styles from './index.module.scss';
import { REGEX, PASSWORD_RULES } from '../../constants/index';
import warningIcon from '/public/icons/warning.svg';
import { GA, getAuthErrorMessage, Mixpanel } from '../../shared/utils';
import { createPlayer } from '../../graphql/mutations';
import PasswordCreate from '../PasswordCreate';
import { Player } from '../../types';
import { AuthContext } from '../../context/auth/context';
import { CreatePlayerMutation } from '../../API';

type AccountCreateProps = {
  email: string;
  setEmail: Function;
  setShowVerification: Function;
  setShowAgeRestriction: Function;
  promoCodeURL?: string;
};

const AccountCreate = ({
  email,
  setEmail,
  setShowVerification,
  setShowAgeRestriction,
  promoCodeURL,
}: AccountCreateProps) => {
  const { setUser } = useContext<{ user: Player; setUser: Function }>(AuthContext);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [birthdayError, setBirthdayError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string>('');
  const { isConnected, disconnect } = /* useAccount()**/ {
    isConnected: false,
    disconnect: () => null,
  };
  const [birthDay, setBirthDay] = useState<number | undefined>(undefined);
  const [birthMonth, setBirthMonth] = useState<number | undefined>(undefined);
  const [birthYear, setBirthYear] = useState<number | undefined>(undefined);
  const failingPasswordsCheck = PASSWORD_RULES.map(({ RULE }) => RULE.test(password)).includes(false);
  const { t } = useTranslation('components');

  // Stops user creating a new account with a different wallet
  useEffect(() => {
    if (isConnected) {
      disconnect();
    }
  }, [disconnect, isConnected]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const signUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (birthYear === undefined || birthMonth === undefined || birthDay === undefined || !isFormValid()) return;

    const dateOfBirth = new Date(birthYear, birthMonth - 1, birthDay);

    const ageCheck = DateTime.local().minus({ years: 16 }).toJSDate();

    if (dateOfBirth > ageCheck) return setShowAgeRestriction(true);

    try {
      await Auth.signUp({
        username: email.toLocaleLowerCase(),
        password,
        attributes: {
          email: email.toLocaleLowerCase(),
          given_name: firstName,
          family_name: lastName,
        },
        autoSignIn: {
          enabled: true,
        },
      }).then(async (cognitoUser: ISignUpResult) => {
        await fetch('/api/create-customer.handler', {
          method: 'POST',
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            email: email.toLocaleLowerCase(),
            locale: 'en-GB',
            billing_address: {
              first_name: firstName,
              last_name: lastName,
            },
          }),
        })
          .then((subscriptionResponse: Response) => {
            return subscriptionResponse.json();
          })
          .then(async (subscriptions: { chargebeeId?: string; stripeId?: string }) => {
            try {
              const dobFormatted = DateTime.fromJSDate(dateOfBirth).toFormat('yyyy-MM-dd');

              const { data: playerData } = (await API.graphql({
                query: createPlayer,
                variables: {
                  input: {
                    id: cognitoUser.userSub,
                    email: email.toLocaleLowerCase(),
                    subscriptionId: subscriptions?.chargebeeId,
                    paymentId: subscriptions?.stripeId,
                    firstName,
                    lastName,
                    dateOfBirth: dobFormatted,
                    achievements: ['hundo Player'],
                  },
                },
                authMode: 'API_KEY',
              })) as {
                data: CreatePlayerMutation;
              };

              setUser({
                type: 'edit',
                payload: playerData?.createPlayer,
              });

              if (playerData?.createPlayer?.id) {
                Mixpanel.identify(email);
                Mixpanel.people.set(
                  {
                    firstName,
                    lastName,
                    dateOfBirth: dobFormatted,
                    email,
                  },
                  email,
                );
                Mixpanel.track('Successfully created an account', email);
              }

              localStorage.setItem('showWelcomeModal', 'true');
              window?.sessionStorage?.setItem('hide-welcome-back', 'true');
              GA.event('user', 'sign_up', 'Player signed up');
              toast.success(t('ACCOUNT_CREATE.SIGN_UP_SUCCESS_MESSAGE'));
              setShowVerification(true);
            } catch (error: unknown) {
              if (error instanceof Error) return error.message;
              console.error(String(error));
              toast.error(String(error));
            }
          })
          .catch((error) => console.error(error));
      });
    } catch (error: unknown) {
      const stringError = String(error);
      const errorMessage = getAuthErrorMessage(error);

      if (stringError.includes('EmptyPassword')) {
        return setPasswordError(errorMessage);
      }

      if (stringError.includes('EmptyUsername')) {
        return setEmailError(errorMessage);
      }

      if (stringError.includes('UsernameExistsException')) {
        return setEmailError(errorMessage);
      }

      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (
      (birthDay !== undefined && (Number(birthDay) < 1 || Number(birthDay) > 31)) ||
      (birthMonth !== undefined && (Number(birthMonth) < 1 || Number(birthMonth) > 12)) ||
      (birthYear !== undefined && Number(birthYear) > new Date().getFullYear())
    ) {
      return setBirthdayError(t('ACCOUNT_CREATE.INVALID_DATE_ERROR_MESSAGE'));
    }

    if (Number(birthYear) && Number(birthMonth) && Number(birthDay)) {
      const bday = DateTime.local(Number(birthYear), Number(birthMonth), Number(birthDay));
      if (bday.isValid) setBirthdayError('');
      else {
        setBirthdayError(t('ACCOUNT_CREATE.INVALID_DATE_ERROR_MESSAGE'));
      }
    } else setBirthdayError('');
  }, [birthDay, birthMonth, birthYear, t]);

  const validateEmail = () => {
    if (!REGEX.EMAIL.test(email)) {
      return setEmailError(t('ACCOUNT_CREATE.INVALID_EMAIL_ERROR_MESSAGE'));
    } else return setEmailError('');
  };

  const validateName = (name: string, cb: Function) => {
    if (!name.length) return;

    if (!REGEX.NAME.test(name)) {
      return cb(t('ACCOUNT_CREATE.INVALID_NAME_ERROR_MESSAGE'));
    } else return cb('');
  };

  const isFormValid = () => {
    if (!REGEX.EMAIL.test(email)) {
      setEmailError(t('ACCOUNT_CREATE.INVALID_EMAIL_ERROR_MESSAGE'));
      return false;
    }
    setEmailError('');

    if (failingPasswordsCheck) {
      setPasswordError(t('ACCOUNT_CREATE.PASSWORD_DOES_NOT_MATCH_ERROR_MESSAGE'));
      return false;
    }
    setPasswordError('');

    if (!Number(birthDay) || !Number(birthMonth) || !Number(birthYear)) {
      setBirthdayError(t('ACCOUNT_CREATE.INVALID_DATE_ERROR_MESSAGE'));
      return false;
    }

    if (String(birthYear).length !== 4) {
      setBirthdayError(t('ACCOUNT_CREATE.INVALID_YEAR_ERROR_MESSAGE'));
      return false;
    }

    const dateOfBirth = new Date(Number(birthYear), Number(birthMonth) - 1, Number(birthDay));

    if (Number(birthDay) && Number(birthMonth) && Number(birthYear) && dateOfBirth > new Date()) {
      setBirthdayError(t('ACCOUNT_CREATE.BIRTHDAY_IN_THE_PAST_ERROR_MESSAGE'));
      return false;
    }
    setBirthdayError('');
    return true;
  };

  const createDisabled =
    failingPasswordsCheck ||
    !termsAccepted ||
    emailError ||
    firstNameError ||
    lastNameError ||
    birthdayError ||
    passwordError ||
    !firstName ||
    !lastName ||
    !birthDay ||
    !birthMonth ||
    !birthYear;

  const dobInput = (
    type: string,
    value: string | undefined,
    setValue: Function,
    error: string | null,
    placeholder: string,
    maxLength: number,
    autoComplete: string,
  ) => {
    return (
      <label>
        {type}
        <input
          className={error ? 'error' : ''}
          type="number"
          name={type}
          required
          value={value}
          placeholder={placeholder}
          onKeyPress={(event: KeyboardEvent<HTMLInputElement>) => {
            if (!/\d/.test(event.key)) event.preventDefault();
          }}
          onChange={(e) => {
            setValue(e.currentTarget.value.slice(0, maxLength));
          }}
          inputMode="numeric"
          autoComplete={autoComplete}
        />
      </label>
    );
  };

  return (
    <form className={styles['account-form']} onSubmit={signUp}>
      <div className="pb-6">
        <div className={styles['name-fields-wrapper']}>
          <label>
            {t('ACCOUNT_CREATE.FIRST_NAME_LABEL')}
            <input
              className={`${styles['name-field']} ${firstNameError && 'error'}`}
              type="text"
              name="firstName"
              required
              value={firstName}
              placeholder="First name"
              onChange={(e) => setFirstName(e.currentTarget.value)}
              onBlur={(e) => validateName(e.currentTarget.value, setFirstNameError)}
              autoCapitalize="sentences"
              autoComplete="given-name"
            />
          </label>

          <label>
            {t('ACCOUNT_CREATE.LAST_NAME_LABEL')}
            <input
              className={`${styles['name-field']} ${lastNameError && 'error'}`}
              type="text"
              name="lastName"
              required
              value={lastName}
              placeholder="Last name"
              onChange={(e) => setLastName(e.currentTarget.value)}
              onBlur={(e) => validateName(e.currentTarget.value, setLastNameError)}
              autoCapitalize="sentences"
              autoComplete="family-name"
            />
          </label>
        </div>

        {(firstNameError || lastNameError) && (
          <div className={`flex pb-4 items-center ${styles['names-error']}`}>
            <Image alt="Warning icon" src={warningIcon} layout="fixed" />
            <p className="error  pl-4">{firstNameError ? firstNameError : lastNameError}</p>
          </div>
        )}

        <label>
          {t('ACCOUNT_CREATE.EMAIL_LABEL')}
          <input
            type="text"
            name="email"
            required
            value={email}
            onBlur={validateEmail}
            className={emailError ? 'error' : ''}
            placeholder="Enter your email here"
            onChange={(e) => setEmail(e.currentTarget.value)}
            autoComplete="email"
            onFocus={() => setEmailError('')}
            autoCapitalize="off"
          />
        </label>

        {emailError && (
          <div className="flex pb-4 items-center">
            <Image alt="Warning icon" src={warningIcon} layout="fixed" />
            <p className="error  pl-4">{emailError}</p>
          </div>
        )}

        <fieldset>
          {t('ACCOUNT_CREATE.DATE_OF_BIRTH_LABEL')}
          <div className={styles['birthday-fields-wrapper']}>
            {dobInput('Day', String(birthDay), setBirthDay, birthdayError, 'DD', 2, 'bday-day')}
            {dobInput('Month', String(birthMonth), setBirthMonth, birthdayError, 'MM', 2, 'bday-month')}
            {dobInput('Year', String(birthYear), setBirthYear, birthdayError, 'YYYY', 4, 'bday-year')}
          </div>
        </fieldset>

        {birthdayError && (
          <div className="flex pb-4">
            <Image alt="Warning icon" src={warningIcon} />
            <p className="error  pl-4">{birthdayError}</p>
          </div>
        )}
        <PasswordCreate
          passwordError={passwordError}
          setPassword={setPassword}
          password={password}
          label={t('ACCOUNT_CREATE.PASSWORD_LABEL')}
          setPasswordError={setPasswordError}
          hideShowPassword
        />
      </div>

      <div className={styles.terms}>
        <label>
          <input type="checkbox" onClick={() => setTermsAccepted(!termsAccepted)} autoComplete="off" />
          {termsAccepted && <span className="checkmark"></span>}
        </label>
        <p>
          {t('ACCOUNT_CREATE.TERMS_LABEL')}
          <Link href="https://www.hundo.careers/terms-of-use" passHref>
            <a rel="noopener noreferrer" target="_blank">
              {t('ACCOUNT_CREATE.TERMS_CTA')}
            </a>
          </Link>
        </p>
      </div>

      <button className="primary full-width" disabled={Boolean(createDisabled)}>
        {t('ACCOUNT_CREATE.CREATE_ACCOUNT_CTA')}
      </button>

      <p className="mt-6 mb-8 text-center">
        {t('ACCOUNT_CREATE.ALREADY_HAVE_ACCOUNT_LABEL')}
        <Link href={`/login${promoCodeURL}`}>
          <a>
            <span className={styles.link}>{t('ACCOUNT_CREATE.LOG_IN_CTA')}</span>
          </a>
        </Link>
      </p>
    </form>
  );
};

export default AccountCreate;
